import { useState } from 'react';
import { useSetRecoilState } from 'recoil';

import { Checkbox } from '@/components/inputs/check-box';
import { RadioTabs } from '@/components/inputs/radio-tabs';
import { FlightServiceGroup } from '@/components/modules/flight-service/group';
import { JourneyLegTab } from '@/components/modules/journey/leg-tab';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/tabs';
import { FlightServiceModel, FlightServicesModel } from '@/lib/models/flight-service/types';
import { transformFlightServiceToItineraryLeg } from '@/lib/transformers/itinerary';
import { selectedItineraryState } from '@/state/itinerary';
import { trackUser } from '@/utils/tracking';

interface FlightServicesListContentProps {
  flightServices: FlightServicesModel[];
}

const FlightServicesListContent = ({ flightServices }: FlightServicesListContentProps) => {
  const initialTabId = flightServices[0].id;
  const isDirectFlight = flightServices.length === 1;

  const [freightOnly, setFreightOnly] = useState(false);
  const [activeWeek, setActiveWeek] = useState(0);
  const setSelectedItinerary = useSetRecoilState(selectedItineraryState);

  const handleRowClick = (flightService: FlightServicesModel, service: FlightServiceModel) => {
    const itinerary = {
      fromId: flightService.fromId,
      toId: flightService.toId,
      type: flightService.type,
      durationSeconds: service.durationSeconds,
      carrierIds: [service.carrierId],
      legs: [transformFlightServiceToItineraryLeg(service, flightService.fromId, flightService.toId)],
    };
    setSelectedItinerary(itinerary);
    // Analytics Event
    trackUser.event('Air Service: Select Row', { carrierId: service.carrierId, flightNum: service.vehicleNumber });
  };

  const handleWeekChange = (weekIndex: number) => {
    setActiveWeek(weekIndex);
    // Analytics Event
    trackUser.event('Air Service: Change Week', { week: weekIndex + 1 });
  };

  const handleToggleFreighter = () => {
    setFreightOnly(!freightOnly);
    // Analytics Event
    trackUser.event('Air Service: Toggle Freight Aircraft', { status: freightOnly ? 'off' : 'on' });
  };

  return (
    <TabsRoot defaultValue={initialTabId} className="relative">
      <div className="absolute right-8 top-0 z-2 flex items-center gap-4">
        <div className="h-7 rounded bg-grey-100 p-1 pr-2">
          <Checkbox value="freightOnly" isSelected={freightOnly} onChange={() => handleToggleFreighter()}>
            <span className="text-sm">Freighters only</span>
          </Checkbox>
        </div>
        <RadioTabs
          name="week-selection-flights"
          size="small"
          options={[
            { value: 0, label: 'This week' },
            { value: 1, label: 'Next week' },
            { value: 2, label: '+ 2 weeks' },
            { value: 3, label: '+ 3 weeks' },
          ]}
          onChange={handleWeekChange}
          selected={activeWeek}
        />
      </div>
      <div className="mx-8">
        <TabsList>
          {flightServices.map((flightService, index) => {
            const { fromId, toId } = flightService;
            return (
              <TabsTrigger value={flightService.id} key={flightService.id}>
                <JourneyLegTab isOnlyLeg={isDirectFlight} index={index} fromId={fromId} toId={toId} />
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>
      {flightServices.map((flightService) => (
        <TabsContent value={flightService.id} key={flightService.id}>
          <FlightServiceGroup
            activeWeek={activeWeek}
            freightOnly={freightOnly}
            services={flightService.service}
            onRowClick={(service) => handleRowClick(flightService, service)}
          />
        </TabsContent>
      ))}
    </TabsRoot>
  );
};

export { FlightServicesListContent };
